import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { CTASection } from '../components/sections/CTASection';
import { 
  Calendar, 
  Truck, 
  ShieldCheck, 
  Package,
  ArrowRight,
  Clock,
  CheckCircle2,
  MessageCircle,
  ClipboardList,
  Home,
  Warehouse
} from 'lucide-react';

const steps = [
  {
    number: "01",
    title: "Reserve Online",
    description: "Students reserve storage online in minutes. Choose your university, select your storage plan, and pick your preferred move-out dates. Our simple booking system makes it easy to get started.",
    icon: Calendar,
    details: [
      "Quick 5-minute signup process",
      "Choose from summer, extended, or ship-to-school options",
      "Select your preferred pickup dates",
      "Receive instant confirmation"
    ],
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=800&auto=format&fit=crop"
  },
  {
    number: "02",
    title: "We Pick Up",
    description: "At the end of the spring semester, our team arrives at your dorm or apartment to pick up your belongings. We handle all the heavy lifting so you can focus on finals and enjoy your last days on campus.",
    icon: Truck,
    details: [
      "Scheduled pickup at your convenience",
      "Our team handles all the heavy lifting",
      "Items are carefully inventoried",
      "Professional handling of all belongings"
    ],
    image: "https://images.unsplash.com/photo-1600518464441-9154a4dea21b?q=80&w=800&auto=format&fit=crop"
  },
  {
    number: "03",
    title: "Secure Summer Storage",
    description: "Your items are stored safely in our secure, climate-appropriate storage facilities throughout the summer. Rest easy knowing your belongings are protected while you're away.",
    icon: ShieldCheck,
    details: [
      "Climate-controlled facilities",
      "24/7 security monitoring",
      "Insurance coverage included",
      "Regular facility inspections"
    ],
    image: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?q=80&w=800&auto=format&fit=crop"
  },
  {
    number: "04",
    title: "Fall Delivery",
    description: "When you return for the fall semester, we deliver everything back to your new dorm or apartment. Start the school year stress-free with all your belongings waiting for you.",
    icon: Package,
    details: [
      "Delivery scheduled around your move-in date",
      "Direct delivery to your new room",
      "Items verified upon delivery",
      "Hassle-free start to the semester"
    ],
    image: "https://images.unsplash.com/photo-1603732525387-198305c6e886?q=80&w=800&auto=format&fit=crop"
  }
];

const timeline = [
  { month: "April", event: "Reserve your storage online", icon: ClipboardList },
  { month: "May", event: "Schedule your pickup date", icon: Calendar },
  { month: "Move-Out", event: "We pick up from your dorm", icon: Home },
  { month: "Summer", event: "Items stored securely", icon: Warehouse },
  { month: "August", event: "Schedule fall delivery", icon: MessageCircle },
  { month: "Move-In", event: "Items delivered to you", icon: Package }
];

export const HowItWorks = () => {
  return (
    <div data-testid="how-it-works-page">
      {/* Hero Section - White */}
      <section className="bg-white section-padding border-b border-gray-100" data-testid="hiw-hero">
        <div className="section-container">
          <motion.div 
            className="max-w-3xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="text-sm font-bold uppercase tracking-widest text-red-600 mb-4 block">
              Simple 4-Step Process
            </span>
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-black text-navy mb-6 leading-tight"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              How Student Storage Works
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              From reservation to delivery, we've made student storage as simple as possible. 
              Here's everything you need to know about our process.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button 
                  className="bg-red-600 hover:bg-red-700 text-white font-bold uppercase tracking-wide px-8 py-4 rounded-lg shadow-lg"
                  data-testid="hiw-reserve-btn"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Reserve Now
                </Button>
              </Link>
              <Link to="/pricing">
                <Button 
                  variant="outline"
                  className="border-2 border-navy text-navy hover:bg-navy hover:text-white font-bold uppercase tracking-wide px-8 py-4 rounded-lg"
                  data-testid="hiw-options-btn"
                >
                  View Pricing
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Timeline Overview */}
      <section className="section-padding bg-gray-50" data-testid="timeline-section">
        <div className="section-container">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 
              className="text-3xl md:text-4xl font-bold text-navy mb-4"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              Your Storage Timeline
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              From spring to fall, here's what your storage journey looks like
            </p>
          </motion.div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-1 bg-gray-200 transform -translate-y-1/2"></div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
              {timeline.map((item, index) => (
                <motion.div
                  key={item.month}
                  className="relative text-center"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <div className="w-16 h-16 mx-auto bg-white rounded-full flex items-center justify-center shadow-lg border-4 border-navy relative z-10">
                    <item.icon className="w-7 h-7 text-navy" />
                  </div>
                  <h4 className="font-bold text-navy mt-4 mb-1" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    {item.month}
                  </h4>
                  <p className="text-sm text-gray-600">{item.event}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Steps */}
      <section className="section-padding bg-white" data-testid="detailed-steps">
        <div className="section-container">
          {steps.map((step, index) => (
            <motion.div
              key={step.number}
              className={`grid lg:grid-cols-2 gap-12 items-center mb-24 last:mb-0 ${
                index % 2 === 1 ? 'lg:flex-row-reverse' : ''
              }`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              data-testid={`detailed-step-${index + 1}`}
            >
              <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                <span className="text-sm font-mono font-bold text-red-accent mb-2 block">
                  STEP {step.number}
                </span>
                <h3 
                  className="text-3xl md:text-4xl font-bold text-navy mb-4"
                  style={{ fontFamily: 'Chivo, sans-serif' }}
                >
                  {step.title}
                </h3>
                <p className="text-gray-600 text-lg mb-6 leading-relaxed">
                  {step.description}
                </p>
                
                <ul className="space-y-3">
                  {step.details.map((detail, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                <div className="relative">
                  <img 
                    src={step.image}
                    alt={step.title}
                    className="rounded-2xl shadow-xl w-full h-[350px] object-cover"
                  />
                  <div className="absolute -top-4 -left-4 w-20 h-20 bg-navy rounded-xl flex items-center justify-center shadow-lg">
                    <step.icon className="w-10 h-10 text-white" />
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FAQ Quick */}
      <section className="section-padding bg-gray-50" data-testid="faq-quick">
        <div className="section-container">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 
              className="text-3xl md:text-4xl font-bold text-navy mb-4"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              Common Questions
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              { q: "When should I reserve?", a: "We recommend reserving at least 2 weeks before finals to guarantee your pickup slot." },
              { q: "What can I store?", a: "Most dorm items: furniture, clothes, books, mini-fridges, and more. We'll provide a full list during booking." },
              { q: "How is pricing determined?", a: "Pricing is based on the number of items and storage duration. We offer competitive student rates." },
              { q: "Is my stuff insured?", a: "Yes! All stored items are covered by our storage protection plan." }
            ].map((faq, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-xl p-6 shadow-sm"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <h4 className="font-bold text-navy mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  {faq.q}
                </h4>
                <p className="text-gray-600 text-sm">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTASection 
        headline="Ready to Get Started?"
        subtext="Reserve your summer storage today and enjoy a stress-free move-out."
        variant="default"
      />
    </div>
  );
};

export default HowItWorks;
